/* Play deck — full-screen slideshow of every slide's live HTML preview.
   Lets founders "sanity check" their deck end-to-end before spending
   credits on layout polish or exporting the .pptx. Uses the shared
   <SlidePreviewBody> component so what you play here is exactly what
   you see in the wizard preview panel — including any custom layouts
   the founder has already applied.

   Controls:
     ←  /  ↑        : previous slide
     → / ↓ / space  : next slide
     Home / End     : jump to first / last
     Escape         : close
     Click stage    : next slide (mobile-friendly)
*/

import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { X, CaretLeft, CaretRight, PlayCircle, DownloadSimple, FilePdf, Sparkle } from "@phosphor-icons/react";
import SlidePreviewBody from "@/components/SlidePreviewBody";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { downloadDeckPdf } from "@/lib/pitchPdf";
import { ExportProgressOverlay } from "@/components/ProgressDonut";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Local palette mirrors the C.orange used across PitchEditor — avoids a
// cross-file theme import for a component this small.
const ORANGE = "#F59E0B";
const TEAL = "#0A8B85";

export default function PlayDeckModal({
  open,
  deck,
  onClose,
  initialSlideNum = 1,
  // Iter105: when provided, we render a prominent "Download .pptx"
  // button in the top bar (visible from every slide) and a large amber
  // CTA overlay on the last slide. This turns Play Deck into the
  // pre-download parity-check step — founders visually verify the deck
  // matches what will land in the .pptx before actually saving it.
  onDownload = null,
  downloadLabel = "Download .pptx",
  downloadTestid = "play-deck-download-btn",
}) {
  // Build a flat playback queue: every core slide, followed immediately by
  // its sub-slides (in stored order). Each queue item carries the visual
  // metadata (slide) plus a marker distinguishing parents from sub-slides
  // so the top-bar and counter can label sub-slides distinctly.
  const playItems = useMemo(() => {
    // Iter76 defensive parity guard — Go-to-Market (slide.num === 7)
    // was removed from the deck flow in iter56, but decks created
    // BEFORE that change still carry a slide.num=7 entry in Mongo.
    // Preview / Play Deck / Create-your-pitch-deck (PPTX) must all
    // render the same set of slides; the PPTX exporter has always
    // filtered num===7 at ingress, so we mirror that here. Removing
    // it once at the queue-build source keeps every downstream navi-
    // gation, counter and progress-dot count in sync automatically.
    const sorted = (deck?.slides || [])
      .filter((s) => s && s.num !== 7)
      .slice()
      .sort((a, b) => a.num - b.num);
    const items = [];
    for (const s of sorted) {
      items.push({ slide: s, isSubSlide: false, subIndex: 0, parentTitle: s.title, parentNum: s.num });
      const extras = s.extras || [];
      extras.forEach((ex, i) => {
        // Match the preview panel's inheritance rule (WizardSlidePreviewAndNav):
        // the sub-slide uses its OWN images if any were uploaded, otherwise
        // it inherits the parent's images so the layout still renders. Without
        // this, Play deck silently ignored sub-slide photos AND diverged from
        // the preview + PPTX export — the exact inconsistency users hit when
        // they clicked "Play deck" after uploading a sub-slide photo.
        const exImages = (ex.images && ex.images.length > 0)
          ? ex.images
          : (s.images || []);
        // Mirror the parent's metadata onto the sub-slide so SlidePreviewBody
        // renders the same header/footer as its parent (num, pillar, layout).
        items.push({
          slide: {
            ...s,
            title: ex.title || `${s.title} (continued)`,
            content: ex.content || "",
            design_prefs: ex.design_prefs || s.design_prefs || {},
            images: exImages,
            _extraId: ex.id,
          },
          isSubSlide: true,
          subIndex: i + 1,
          totalSubs: extras.length,
          parentTitle: s.title,
          parentNum: s.num,
        });
      });
    }
    return items;
  }, [deck]);

  const [idx, setIdx] = useState(0);

  /* Exact view — real rendered pages from the PPTX→PDF pipeline, cached
     server-side per deck version. FREE. Draft view stays instant while
     the exact render happens in the background. */
  const [exact, setExact] = useState({ status: "idle", ck: null, pageCount: 0 });
  const [mode, setMode] = useState("draft"); // draft | exact
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfDone, setPdfDone] = useState(false);
  const pollRef = useRef(null);
  const deckId = deck?.deck_id;

  useEffect(() => {
    if (!open || !deckId) return undefined;
    let stop = false;
    (async () => {
      try {
        const { data } = await api.post(`/pitch/decks/${deckId}/play-render`);
        if (stop) return;
        if (data.status === "ready") {
          setExact({ status: "ready", ck: data.cache_key, pageCount: data.page_count });
          return;
        }
        if (data.status === "failed") { setExact({ status: "failed", ck: null, pageCount: 0 }); return; }
        setExact({ status: "processing", ck: data.cache_key, pageCount: 0 });
        const started = Date.now();
        pollRef.current = setInterval(async () => {
          if (stop || Date.now() - started > 150000) { clearInterval(pollRef.current); return; }
          try {
            const s = await api.get(`/pitch/decks/${deckId}/play-render/status`, { params: { ck: data.cache_key } });
            if (s.data.status === "ready") {
              clearInterval(pollRef.current);
              if (!stop) setExact({ status: "ready", ck: data.cache_key, pageCount: s.data.page_count });
            } else if (s.data.status === "failed") {
              clearInterval(pollRef.current);
              if (!stop) setExact({ status: "failed", ck: null, pageCount: 0 });
            }
          } catch { /* transient — keep polling */ }
        }, 2500);
      } catch { if (!stop) setExact({ status: "failed", ck: null, pageCount: 0 }); }
    })();
    return () => { stop = true; if (pollRef.current) clearInterval(pollRef.current); };
  }, [open, deckId]);

  const exactMode = mode === "exact" && exact.status === "ready";
  const hasPaidExport = !!(deck?.exported_at || (deck?.export_count || 0) > 0);

  const downloadPdf = async () => {
    setPdfBusy(true);
    setPdfDone(false);
    try {
      await downloadDeckPdf(deckId, deck?.company_name);
      setPdfDone(true);
      await new Promise((r) => setTimeout(r, 700));
      toast.success("PDF downloaded — free with your PPTX export", {
        description: "A copy has been emailed to you and saved under My Exports on your dashboard.",
        duration: 9000,
      });
    } catch (e) {
      if (e?.stillProcessing) {
        toast.info("Your PDF is still rendering — we'll finish it in the background", {
          description: "The finished file will appear under My Exports on your dashboard and be emailed to you shortly.",
          duration: 15000,
        });
      } else {
        const detail = e?.response?.data?.detail;
        toast.error(detail?.message || e.message || "PDF download failed");
      }
    }
    setPdfBusy(false);
  };

  // Sync initial slide when modal opens — jumps to the PARENT slide item
  // (never lands on a sub-slide by default).
  useEffect(() => {
    if (!open) return;
    const start = playItems.findIndex((it) => !it.isSubSlide && it.parentNum === initialSlideNum);
    setIdx(start >= 0 ? start : 0);
  }, [open, initialSlideNum, playItems]);

  const goPrev = useCallback(() => setIdx((i) => Math.max(0, i - 1)), []);
  const totalItems = exactMode ? exact.pageCount : playItems.length;
  const goNext = useCallback(() => setIdx((i) => Math.min(totalItems - 1, i + 1)), [totalItems]);
  const goFirst = useCallback(() => setIdx(0), []);
  const goLast = useCallback(() => setIdx(totalItems - 1), [totalItems]);

  // Clamp position when switching between draft/exact (different counts)
  useEffect(() => { setIdx((i) => Math.min(i, Math.max(0, totalItems - 1))); }, [totalItems]);

  // Keyboard navigation (only while modal is open)
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose?.(); return; }
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); goPrev(); return; }
      if (e.key === "ArrowRight" || e.key === "ArrowDown" || e.key === " ") { e.preventDefault(); goNext(); return; }
      if (e.key === "Home") { e.preventDefault(); goFirst(); return; }
      if (e.key === "End") { e.preventDefault(); goLast(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, goPrev, goNext, goFirst, goLast]);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open || playItems.length === 0) return null;

  const item = playItems[Math.min(idx, playItems.length - 1)];
  const slide = item.slide;
  const bullets = (slide?.content || "")
    .split("\n")
    .map((l) => l.replace(/^[-*•●▪]\s*/, "").replace(/^\d+[.)]\s*/, "").trim())
    .filter(Boolean);
  const primary = deck.brand_color || "#0A8B85";
  const layout = ((slide?.design_prefs || {}).layout || "").trim();
  const total = totalItems;
  const isFirst = idx === 0;
  const isLast = idx === total - 1;

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col"
      style={{ backgroundColor: "rgba(2, 10, 14, 0.94)" }}
      data-testid="play-deck-modal"
      role="dialog"
      aria-label="Deck slideshow"
    >
      <ExportProgressOverlay active={pdfBusy} done={pdfDone}
        title="Preparing your PDF"
        subtitle="Rendering print-quality pages — usually 10–30 seconds."
        testid="pdf-export-progress" />
      {/* Top bar — company name, slide counter, close */}
      <div className="flex items-center justify-between px-6 py-3 text-white/85"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em]">
          <PlayCircle size={16} weight="fill" style={{ color: ORANGE }} />
          <span className="font-bold">Playing</span>
          <span className="text-white/45">·</span>
          <span className="text-white/70">{deck.company_name || "Your deck"}</span>
        </div>
        <div className="text-[11px] font-mono-num tabular-nums text-white/70" data-testid="play-deck-counter">
          {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </div>
        <div className="flex items-center gap-2">
          {/* Draft / Exact toggle — Exact = real PPTX render, guaranteed
              identical to the download. */}
          <div className="flex items-center rounded-md overflow-hidden text-[11px] font-bold"
            style={{ border: "1px solid rgba(255,255,255,0.14)" }} data-testid="play-deck-mode-toggle">
            <button onClick={() => setMode("draft")} data-testid="play-deck-mode-draft"
              className="px-2.5 py-1.5 transition-colors"
              style={{ backgroundColor: mode === "draft" ? "rgba(255,255,255,0.14)" : "transparent", color: mode === "draft" ? "#fff" : "rgba(255,255,255,0.5)" }}>
              Draft
            </button>
            <button
              onClick={() => exact.status === "ready" && setMode("exact")}
              data-testid="play-deck-mode-exact"
              disabled={exact.status !== "ready"}
              title={exact.status === "processing" ? "Rendering your exact deck…" : exact.status === "failed" ? "Exact render unavailable" : "Exactly as your .pptx"}
              className="px-2.5 py-1.5 transition-colors inline-flex items-center gap-1.5"
              style={{ backgroundColor: mode === "exact" && exact.status === "ready" ? ORANGE : "transparent", color: mode === "exact" && exact.status === "ready" ? "#000" : exact.status === "ready" ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.3)" }}>
              <Sparkle size={11} weight="fill" />
              {exact.status === "processing" ? "Exact · rendering…" : "Exact (.pptx)"}
            </button>
          </div>
          {hasPaidExport && (
            <button
              onClick={downloadPdf}
              disabled={pdfBusy}
              data-testid="play-deck-download-pdf-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-bold transition-colors"
              style={{ backgroundColor: "rgba(245,158,11,0.14)", color: ORANGE, border: `1px solid ${ORANGE}` }}
            >
              <FilePdf size={13} weight="bold" /> {pdfBusy ? "Preparing…" : "PDF · free"}
            </button>
          )}
          {onDownload && (
            <button
              onClick={onDownload}
              data-testid={downloadTestid}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-bold transition-colors"
              style={{ backgroundColor: TEAL, color: "#fff" }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#0BA39C"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = TEAL; }}
            >
              <DownloadSimple size={13} weight="bold" /> {downloadLabel}
            </button>
          )}
          <button
            onClick={onClose}
            data-testid="play-deck-close-btn"
            className="p-1.5 rounded-md text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Close slideshow"
          >
            <X size={20} weight="bold" />
          </button>
        </div>
      </div>

      {/* Stage — centered 16:9 card */}
      <div
        className="flex-1 flex items-center justify-center px-6 py-6 relative select-none"
        onClick={goNext}
        style={{ cursor: isLast ? "default" : "pointer" }}
        data-testid="play-deck-stage"
      >
        <div
          className="rounded-xl overflow-hidden shadow-2xl bg-white"
          style={{
            width: "min(1280px, 90vw, calc((100vh - 180px) * 16 / 9))",
            aspectRatio: "16 / 9",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
          onClick={(e) => e.stopPropagation() /* clicks on prev/next arrows handle their own */}
        >
          {exactMode ? (
            <img
              src={`${API_BASE}/api/pitch/decks/${deckId}/play-render/page/${idx + 1}?ck=${encodeURIComponent(exact.ck)}`}
              alt={`Slide ${idx + 1} — exact render`}
              data-testid="play-deck-exact-page"
              className="w-full h-full object-contain bg-white"
              draggable={false}
            />
          ) : (
            <SlidePreviewBody
              deck={deck}
              slide={slide}
              bullets={bullets}
              primary={primary}
              layout={layout}
            />
          )}
        </div>

        {/* Prev / Next arrow overlays */}
        {!isFirst && (
          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            data-testid="play-deck-prev-btn"
            aria-label="Previous slide"
            className="absolute left-6 top-1/2 -translate-y-1/2 p-3 rounded-full transition-all"
            style={{ backgroundColor: "rgba(255,255,255,0.08)", color: "#fff", backdropFilter: "blur(6px)" }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.16)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)"; }}
          >
            <CaretLeft size={22} weight="bold" />
          </button>
        )}
        {!isLast && (
          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            data-testid="play-deck-next-btn"
            aria-label="Next slide"
            className="absolute right-6 top-1/2 -translate-y-1/2 p-3 rounded-full transition-all"
            style={{ backgroundColor: "rgba(255,255,255,0.08)", color: "#fff", backdropFilter: "blur(6px)" }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.16)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)"; }}
          >
            <CaretRight size={22} weight="bold" />
          </button>
        )}
      </div>

      {/* Bottom bar — slide title + progress dots + keyboard hint */}
      <div className="px-6 py-4 flex items-center justify-between gap-6 text-white/75"
        style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        {/* Iter105: floating pre-download CTA on the last slide. Only
            shows when the parent passed `onDownload` (i.e. Play Deck was
            opened as the pre-download parity-check step). Keeps founders
            oriented — "you've reviewed every slide, ready to save?" */}
        {onDownload && isLast && (
          <div
            className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3 rounded-full pl-3 pr-2 py-1.5"
            style={{
              bottom: 76,
              backgroundColor: "rgba(10, 139, 133, 0.14)",
              border: `1px solid ${TEAL}`,
              boxShadow: "0 12px 40px rgba(10, 139, 133, 0.35)",
            }}
            data-testid="play-deck-final-cta"
          >
            <span className="text-[12px] font-semibold text-white">
              Everything look right?
            </span>
            <button
              onClick={onDownload}
              data-testid="play-deck-final-download-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-bold transition-colors"
              style={{ backgroundColor: TEAL, color: "#fff" }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#0BA39C"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = TEAL; }}
            >
              <DownloadSimple size={13} weight="bold" /> {downloadLabel}
            </button>
          </div>
        )}
        <div className="flex-1 min-w-0">
          {exactMode ? (
            <>
              <div className="text-[9px] font-bold uppercase tracking-[0.22em]" style={{ color: ORANGE }}>
                Exact render · identical to your .pptx
              </div>
              <div className="text-sm font-semibold truncate mt-0.5 text-white" data-testid="play-deck-slide-title">
                Page {idx + 1} of {total}
              </div>
            </>
          ) : (
            <>
              <div className="text-[9px] font-bold uppercase tracking-[0.22em] text-white/50">
                Slide {item.parentNum}{slide.pillar ? ` · ${slide.pillar}` : ""}
                {item.isSubSlide && (
                  <span data-testid="play-deck-sub-badge"
                    className="ml-2 rounded px-1.5 py-0.5 tracking-widest"
                    style={{ backgroundColor: "rgba(245, 158, 11, 0.18)", color: ORANGE, border: "1px solid rgba(245, 158, 11, 0.4)" }}>
                    ↳ SUB {item.subIndex} / {item.totalSubs}
                  </span>
                )}
              </div>
              <div className="text-sm font-semibold truncate mt-0.5 text-white" data-testid="play-deck-slide-title">
                {slide.title}
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-1.5" role="tablist" aria-label="Slide progress">
          {exactMode ? (
            Array.from({ length: total }, (_, i) => (
              <button key={`pg-${i}`} onClick={() => setIdx(i)}
                data-testid={`play-deck-page-dot-${i + 1}`}
                aria-label={`Jump to page ${i + 1}`}
                className="rounded-full transition-all"
                style={{
                  width: i === idx ? 22 : 8, height: 8,
                  backgroundColor: i === idx ? ORANGE : i < idx ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.18)",
                  cursor: "pointer",
                }} />
            ))
          ) : (
            playItems.map((it, i) => (
            <button
              key={`${it.parentNum}-${it.isSubSlide ? it.subIndex : "main"}`}
              onClick={() => setIdx(i)}
              data-testid={it.isSubSlide
                ? `play-deck-dot-${it.parentNum}-sub-${it.subIndex}`
                : `play-deck-dot-${it.parentNum}`}
              aria-label={it.isSubSlide
                ? `Jump to slide ${it.parentNum} sub-slide ${it.subIndex}`
                : `Jump to slide ${it.parentNum}`}
              title={it.isSubSlide ? `${it.parentTitle} · sub ${it.subIndex} of ${it.totalSubs}` : it.parentTitle}
              className="rounded-full transition-all"
              style={{
                // Sub-slide dots are smaller so the eye can quickly tell
                // which items are continuations vs core slides.
                width: i === idx ? 22 : (it.isSubSlide ? 5 : 8),
                height: it.isSubSlide ? 5 : 8,
                backgroundColor: i === idx
                  ? ORANGE
                  : i < idx
                    ? (it.isSubSlide ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.55)")
                    : (it.isSubSlide ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.18)"),
                cursor: "pointer",
              }}
            />
            ))
          )}
        </div>
        <div className="text-[10px] text-white/45 hidden md:flex items-center gap-3">
          <span>
            <kbd className="px-1.5 py-0.5 rounded font-mono-num" style={{ backgroundColor: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}>←</kbd>{" "}
            <kbd className="px-1.5 py-0.5 rounded font-mono-num" style={{ backgroundColor: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}>→</kbd> navigate
          </span>
          <span>
            <kbd className="px-1.5 py-0.5 rounded font-mono-num" style={{ backgroundColor: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}>esc</kbd> close
          </span>
        </div>
      </div>
    </div>
  );
}
